import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, Form, Button, Segment, Message, Loader } from 'semantic-ui-react';
import { API, isAdmin, showError } from '../../helpers';
import { streamChatCompletions } from '../../helpers/streamChat';

const STORAGE_PREFIX = 'mindhub:default:playground';
const STORAGE_KEYS = {
  channelId: `${STORAGE_PREFIX}:channelId`,
  channelModel: `${STORAGE_PREFIX}:channelModel`,
  userModel: `${STORAGE_PREFIX}:userModel`
};

function readStorage(key) {
  try {
    return localStorage.getItem(key) || '';
  } catch {
    return '';
  }
}

function writeStorage(key, value) {
  try {
    if (value) {
      localStorage.setItem(key, value);
    } else {
      localStorage.removeItem(key);
    }
  } catch {
    // Ignore localStorage runtime errors (privacy mode, quota, etc.).
  }
}

function getModelStorageKey(isChannelMode) {
  return isChannelMode ? STORAGE_KEYS.channelModel : STORAGE_KEYS.userModel;
}

function parseModelList(modelsStr) {
  if (!modelsStr || typeof modelsStr !== 'string') {
    return [];
  }
  return modelsStr
    .split(/[,\n]/)
    .map((s) => s.trim())
    .filter(Boolean);
}

function filterModelsByToken(available, tokenModelsStr) {
  if (!tokenModelsStr || !String(tokenModelsStr).trim()) {
    return available;
  }
  const allowed = new Set(parseModelList(tokenModelsStr));
  return available.filter((m) => allowed.has(m));
}

export default function Playground({ mode }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isChannelMode = mode === 'channel';

  const [tokens, setTokens] = useState([]);
  const [channels, setChannels] = useState([]);
  const [userGroup, setUserGroup] = useState('');
  const [selectedTokenId, setSelectedTokenId] = useState('');
  const [selectedChannelId, setSelectedChannelId] = useState(() => readStorage(STORAGE_KEYS.channelId));
  const [model, setModel] = useState(() => readStorage(getModelStorageKey(isChannelMode)));
  const [availableModels, setAvailableModels] = useState([]);
  const [temperature, setTemperature] = useState(0.7);
  const [topP, setTopP] = useState(1);
  const [frequencyPenalty, setFrequencyPenalty] = useState(0);
  const [presencePenalty, setPresencePenalty] = useState(0);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (isChannelMode && !isAdmin()) {
      navigate('/dashboard');
    }
  }, [isChannelMode, navigate]);

  useEffect(() => {
    (async () => {
      const res = await API.get('/api/token/?p=0');
      const { success, message, data } = res.data;
      if (!success) {
        showError(message);
        return;
      }
      const list = data || [];
      setTokens(list);
      setSelectedTokenId((cur) => {
        if (cur && list.some((x) => String(x.id) === String(cur))) {
          return cur;
        }
        const first = list.find((x) => x.status === 1) || list[0];
        return first ? String(first.id) : '';
      });
    })();
  }, []);

  useEffect(() => {
    (async () => {
      const res = await API.get('/api/user/self');
      const { success, data } = res.data;
      if (success && data?.group) {
        setUserGroup(data.group);
      }
    })();
  }, []);

  useEffect(() => {
    if (!isChannelMode) {
      (async () => {
        const res = await API.get('/api/user/available_models');
        const { success, message, data } = res.data;
        if (!success) {
          showError(message);
          return;
        }
        setAvailableModels(Array.isArray(data) ? data : []);
      })();
    }
  }, [isChannelMode]);

  useEffect(() => {
    if (!isChannelMode) {
      return;
    }
    (async () => {
      const res = await API.get('/api/channel/?p=0');
      const { success, message, data } = res.data;
      if (!success) {
        showError(message);
        return;
      }
      setChannels((data || []).filter((c) => c.status === 1));
    })();
  }, [isChannelMode]);

  useEffect(() => {
    if (!isChannelMode || !channels.length) {
      return;
    }
    if (!channels.some((c) => String(c.id) === String(selectedChannelId))) {
      setSelectedChannelId(String(channels[0].id));
    }
  }, [isChannelMode, channels, selectedChannelId]);

  useEffect(() => {
    setModel(readStorage(getModelStorageKey(isChannelMode)));
    if (isChannelMode) {
      setSelectedChannelId(readStorage(STORAGE_KEYS.channelId));
      return;
    }
    setSelectedChannelId('');
  }, [isChannelMode]);

  useEffect(() => {
    if (!isChannelMode) {
      return;
    }
    writeStorage(STORAGE_KEYS.channelId, selectedChannelId);
  }, [isChannelMode, selectedChannelId]);

  useEffect(() => {
    writeStorage(getModelStorageKey(isChannelMode), model);
  }, [isChannelMode, model]);

  const selectedToken = useMemo(() => tokens.find((x) => String(x.id) === String(selectedTokenId)), [tokens, selectedTokenId]);

  const selectedChannel = useMemo(
    () => channels.find((c) => String(c.id) === String(selectedChannelId)),
    [channels, selectedChannelId]
  );

  const channelModelOptions = useMemo(() => {
    if (!selectedChannel?.models) {
      return [];
    }
    return parseModelList(selectedChannel.models);
  }, [selectedChannel]);

  const modelOptions = useMemo(() => {
    if (isChannelMode) {
      return channelModelOptions;
    }
    return filterModelsByToken(availableModels, selectedToken?.models);
  }, [isChannelMode, channelModelOptions, availableModels, selectedToken]);

  useEffect(() => {
    if (!modelOptions.length) {
      return;
    }
    if (!model || !modelOptions.includes(model)) {
      setModel(modelOptions[0]);
    }
  }, [modelOptions, model]);

  const authorization = useMemo(() => {
    if (!selectedToken?.key) {
      return '';
    }
    const base = `Bearer sk-${selectedToken.key}`;
    if (isChannelMode && selectedChannelId) {
      return `${base}-${selectedChannelId}`;
    }
    return base;
  }, [selectedToken, isChannelMode, selectedChannelId]);

  const handleSend = async () => {
    const text = input.trim();
    if (!text) {
      return;
    }
    if (!authorization) {
      showError(t('playground.select_token'));
      return;
    }
    if (!model) {
      showError(t('playground.select_model'));
      return;
    }
    if (isChannelMode && !selectedChannelId) {
      showError(t('playground.select_channel'));
      return;
    }

    const userMsg = { role: 'user', content: text };
    const nextMessages = [...messages, userMsg];
    const assistantIndex = nextMessages.length;
    setMessages([...nextMessages, { role: 'assistant', content: '' }]);
    setInput('');
    setSending(true);

    try {
      const content = await streamChatCompletions({
        authorization,
        messages: [...nextMessages],
        model,
        temperature,
        top_p: topP,
        frequency_penalty: frequencyPenalty,
        presence_penalty: presencePenalty,
        onDelta: (delta) => {
          setMessages((prev) => {
            const copy = [...prev];
            const idx = copy.length - 1;
            if (idx >= 0 && copy[idx]?.role === 'assistant') {
              copy[idx] = {
                role: 'assistant',
                content: (copy[idx].content || '') + delta
              };
            }
            return copy;
          });
        }
      });
      setMessages((prev) => {
        const copy = [...prev];
        if (copy[assistantIndex]) {
          copy[assistantIndex] = {
            role: 'assistant',
            content: content || copy[assistantIndex].content || ''
          };
        }
        return copy;
      });
    } catch (e) {
      setMessages((prev) => prev.slice(0, assistantIndex));
      showError(e?.message || String(e));
    } finally {
      setSending(false);
    }
  };

  const tokenOptions = tokens.map((x) => ({
    key: x.id,
    value: String(x.id),
    text: `${x.name} (#${x.id})`,
    disabled: x.status !== 1
  }));

  const channelOptions = channels.map((c) => ({
    key: c.id,
    value: String(c.id),
    text: `${c.name} (#${c.id})`
  }));

  const modelDropdownOptions = modelOptions.map((m) => ({ key: m, value: m, text: m }));

  return (
    <div className='dashboard-container'>
      <Card fluid className='chart-card'>
        <Card.Content>
          <Card.Header className='header'>
            {isChannelMode ? t('playground.title_channel') : t('playground.title_user')}
          </Card.Header>
          <div style={{ marginTop: '1em', display: 'flex', gap: '1rem', alignItems: 'stretch', flexWrap: 'wrap' }}>
            <div style={{ width: 450, maxWidth: '100%', flex: '0 0 450px' }}>
              <Segment>
                <h4>{t('playground.model_config')}</h4>
                <Message info size='small' content={isChannelMode ? t('playground.hint_channel') : t('playground.hint_user')} />
                <Form>
                  <Form.Field>
                    <label>{t('playground.token')}</label>
                    <Form.Dropdown
                      fluid
                      selection
                      options={tokenOptions}
                      value={selectedTokenId}
                      onChange={(_, { value }) => setSelectedTokenId(value)}
                    />
                  </Form.Field>
                  {isChannelMode && (
                    <Form.Field>
                      <label>{t('playground.channel')}</label>
                      <Form.Dropdown
                        fluid
                        selection
                        options={channelOptions}
                        value={selectedChannelId}
                        onChange={(_, { value }) => setSelectedChannelId(value)}
                        placeholder={t('playground.select_channel')}
                      />
                    </Form.Field>
                  )}
                  <p style={{ color: '#666', fontSize: '0.9em' }}>
                    {t('playground.group')}: {userGroup || '—'}
                  </p>
                  <Form.Field>
                    <label>{t('playground.model')}</label>
                    <Form.Dropdown
                      fluid
                      selection
                      search
                      options={modelDropdownOptions}
                      value={model}
                      onChange={(_, { value }) => setModel(value)}
                    />
                  </Form.Field>
                  <Form.Field>
                    <label>Temperature: {temperature}</label>
                    <input
                      type='range'
                      min={0}
                      max={2}
                      step={0.05}
                      value={temperature}
                      onChange={(e) => setTemperature(Number(e.target.value))}
                    />
                  </Form.Field>
                  <Form.Field>
                    <label>Top P: {topP}</label>
                    <input type='range' min={0} max={1} step={0.01} value={topP} onChange={(e) => setTopP(Number(e.target.value))} />
                  </Form.Field>
                  <Form.Field>
                    <label>Frequency Penalty: {frequencyPenalty}</label>
                    <input
                      type='range'
                      min={-2}
                      max={2}
                      step={0.1}
                      value={frequencyPenalty}
                      onChange={(e) => setFrequencyPenalty(Number(e.target.value))}
                    />
                  </Form.Field>
                  <Form.Field>
                    <label>Presence Penalty: {presencePenalty}</label>
                    <input
                      type='range'
                      min={-2}
                      max={2}
                      step={0.1}
                      value={presencePenalty}
                      onChange={(e) => setPresencePenalty(Number(e.target.value))}
                    />
                  </Form.Field>
                </Form>
              </Segment>
            </div>
            <div style={{ flex: '1 1 0%', minWidth: 0 }}>
              <Segment style={{ minHeight: 480, display: 'flex', flexDirection: 'column' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <h4>{t('playground.ai_chat')}</h4>
                  <span style={{ color: '#888' }}>{model || '—'}</span>
                </div>
                {sending && <Loader active inline size='small' style={{ marginBottom: 8 }} />}
                <div
                  style={{
                    flex: 1,
                    overflowY: 'auto',
                    background: '#f4f4f5',
                    borderRadius: 8,
                    padding: 12,
                    marginBottom: 12
                  }}
                >
                  {messages.length === 0 && (
                    <p style={{ textAlign: 'center', color: '#999', marginTop: 40 }}>{t('playground.empty_chat')}</p>
                  )}
                  {messages.map((m, i) => (
                    <div
                      key={i}
                      style={{
                        display: 'flex',
                        justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start',
                        marginBottom: 12
                      }}
                    >
                      <div
                        style={{
                          maxWidth: '85%',
                          padding: '10px 14px',
                          borderRadius: 10,
                          background: m.role === 'user' ? '#2185d0' : '#fff',
                          color: m.role === 'user' ? '#fff' : '#333',
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                          boxShadow: m.role === 'assistant' ? '0 1px 2px rgba(0,0,0,0.08)' : 'none'
                        }}
                      >
                        {m.content || (m.role === 'assistant' && sending ? '…' : '')}
                      </div>
                    </div>
                  ))}
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
                  <Button
                    icon='trash'
                    type='button'
                    disabled={sending || !messages.length}
                    onClick={() => setMessages([])}
                    content={t('playground.clear')}
                  />
                  <Form style={{ flex: 1, margin: 0 }}>
                    <Form.TextArea
                      rows={2}
                      placeholder={t('playground.placeholder')}
                      value={input}
                      onChange={(e) => setInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          if (!sending) {
                            handleSend();
                          }
                        }
                      }}
                    />
                  </Form>
                  <Button primary type='button' disabled={sending} onClick={handleSend} content={t('playground.send')} />
                </div>
              </Segment>
            </div>
          </div>
        </Card.Content>
      </Card>
    </div>
  );
}
